<template>
  <ion-app>
    <!-- Bannière consentement cookies RGPD / PIPEDA -->
    <transition name="slide-up">
      <div v-if="showCookieBanner" class="cookie-banner">
        <p class="cookie-text">{{ t('app.cookie_msg') }}</p>
        <div class="cookie-actions">
          <button class="cookie-btn cookie-btn-accept" @click="acceptCookies">{{ t('app.cookie_accept') }}</button>
          <button class="cookie-btn cookie-btn-decline" @click="declineCookies">{{ t('app.cookie_decline') }}</button>
        </div>
      </div>
    </transition>
    <transition name="slide-down">
      <div v-if="!isOnline" class="offline-banner">
        <span class="offline-icon">📡</span>
        <span class="offline-text">{{ t('app.offline_mode') }}</span>
        <span class="offline-sub">{{ t('app.offline_cached_data') }}</span>
      </div>
    </transition>
    <transition name="slide-down">
      <div v-if="isOnline && wasOffline" class="online-banner" @click="wasOffline = false">
        <span>✅ {{ t('app.back_online') }}</span>
      </div>
    </transition>
    <ion-router-outlet />
  </ion-app>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { useNetwork } from '@/composables/useNetwork';
import { useI18n } from '@/utils/i18n';

const { t } = useI18n();
const { isOnline, wasOffline } = useNetwork();
const showCookieBanner = ref(false);

onMounted(() => {
  const consent = localStorage.getItem('recash_cookie_consent');
  if (!consent) showCookieBanner.value = true;
});

const acceptCookies = () => {
  localStorage.setItem('recash_cookie_consent', 'accepted');
  showCookieBanner.value = false;
};

const declineCookies = () => {
  localStorage.setItem('recash_cookie_consent', 'declined');
  showCookieBanner.value = false;
};

watch(isOnline, (online) => {
  if (online && wasOffline.value) {
    setTimeout(() => { wasOffline.value = false; }, 3000);
  }
});
</script>

<style>
.offline-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: #2C2C2A;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.offline-icon { font-size: 16px; }
.offline-text { font-weight: 600; }
.offline-sub {
  font-size: 11px;
  opacity: 0.7;
  margin-left: auto;
}

.online-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: #1E7F5E;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9998;
  background: #1a1a2e;
  color: #ffffff;
  padding: 14px 16px;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.3);
}

.cookie-text {
  font-size: 12px;
  margin: 0 0 10px;
  opacity: 0.9;
  line-height: 1.5;
}

.cookie-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.cookie-btn {
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.cookie-btn-accept {
  background: #0EA371;
  color: #fff;
}

.cookie-btn-decline {
  background: transparent;
  color: #aaa;
  border: 1px solid #444;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>